import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { formatPhone, formatPhoneInput } from "@/lib/phone";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { UserPlus, Trash2, Plus, Pencil, UserCircle2, Mail, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const COLOR_OPTIONS = [
  { v: "White",   hex: "#f8f8f8" },
  { v: "Black",   hex: "#111111" },
  { v: "Silver",  hex: "#c0c0c0" },
  { v: "Gray",    hex: "#737373" },
  { v: "Red",     hex: "#d62828" },
  { v: "Blue",    hex: "#1d4ed8" },
  { v: "Green",   hex: "#234123" },
  { v: "Yellow",  hex: "#facc15" },
  { v: "Orange",  hex: "#FF6D00" },
  { v: "Brown",   hex: "#7c4a1e" },
];

function ColorSwatch({ name }) {
  const c = COLOR_OPTIONS.find((o) => o.v === name);
  if (!c) return <span className="text-muted-foreground">—</span>;
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="h-3.5 w-3.5 rounded-full border border-border" style={{ background: c.hex }} />
      <span>{c.v}</span>
    </span>
  );
}

const EMPTY_DRIVER = { name: "", email: "", phone: "", password: "" };
const EMPTY_VEHICLE = { plate: "", model: "", color: "White" };

export default function AdminDrivers() {
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);

  const [openAdd, setOpenAdd] = useState(false);
  const [addForm, setAddForm] = useState({ ...EMPTY_DRIVER, ...EMPTY_VEHICLE });

  const [editRow, setEditRow] = useState(null); // { driver, vehicle }
  const [editForm, setEditForm] = useState({});

  const [busy, setBusy] = useState(false);
  // Email-app-invite flow (replaces the legacy phone/SMS share link).
  // Mirrors AdminUsers — single endpoint POST /users/{id}/send-invite-email,
  // and the result dialog handles both "email sent" and "fallback magic link" cases.
  const [emailingId, setEmailingId] = useState(null);
  const [inviteResult, setInviteResult] = useState(null); // { user, email_sent, to, expires_in_days, fallback_url, error }

  const sendInviteEmail = async (driver) => {
    if (!driver.email) { toast.error("This driver has no email on file"); return; }
    setEmailingId(driver.id);
    try {
      const { data } = await api.post(`/users/${driver.id}/send-invite-email`);
      setInviteResult({ user: driver, ...data });
      if (data.email_sent) toast.success(`Invite emailed to ${data.to}`);
      else toast.warning("Couldn't email — share the magic link manually");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setEmailingId(null); }
  };

  const load = () =>
    Promise.all([
      api.get("/users", { params: { role: "driver", include_inactive: true } }),
      api.get("/vehicles"),
    ]).then(([u, v]) => { setDrivers(u.data); setVehicles(v.data); });
  useEffect(() => { load(); }, []);

  const toggleActive = async (drv, next) => {
    try {
      await api.patch(`/users/${drv.id}`, { active: next });
      toast.success(next ? `${drv.name} activated` : `${drv.name} deactivated`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const vehicleByDriver = useMemo(() => {
    const m = {};
    for (const v of vehicles) if (v.driver_id) m[v.driver_id] = v;
    return m;
  }, [vehicles]);

  /* ---------- Add (driver + optional vehicle in one step) ---------- */
  const submitAdd = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data: drv } = await api.post("/users", {
        name: addForm.name, email: addForm.email, password: addForm.password,
        phone: addForm.phone, role: "driver", pharmacy_id: null,
      });
      if (addForm.plate.trim() || addForm.model.trim()) {
        await api.post("/vehicles", {
          plate: addForm.plate || "—", model: addForm.model || "—",
          color: addForm.color || null, driver_id: drv.id,
        });
      }
      toast.success("Driver added");
      setAddForm({ ...EMPTY_DRIVER, ...EMPTY_VEHICLE });
      setOpenAdd(false);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  /* ---------- Edit row ---------- */
  const startEdit = (driver) => {
    const v = vehicleByDriver[driver.id] || {};
    setEditRow({ driver, vehicle: v });
    setEditForm({
      name: driver.name || "",
      email: driver.email || "",
      phone: driver.phone || "",
      password: "",
      plate: v.plate || "",
      model: v.model || "",
      color: v.color || "White",
    });
  };
  const cancelEdit = () => { setEditRow(null); setEditForm({}); };

  const submitEdit = async (e) => {
    e.preventDefault();
    if (!editRow) return;
    setBusy(true);
    try {
      const drv = editRow.driver;
      const updates = {};
      if (editForm.name !== drv.name) updates.name = editForm.name;
      if (editForm.email !== drv.email) updates.email = editForm.email;
      if ((editForm.phone || "") !== (drv.phone || "")) updates.phone = editForm.phone;
      if (editForm.password) updates.password = editForm.password;
      if (Object.keys(updates).length) await api.patch(`/users/${drv.id}`, updates);

      const v = editRow.vehicle;
      const hasVeh = !!v?.id;
      const vehWanted = (editForm.plate || editForm.model || editForm.color);
      if (hasVeh && vehWanted) {
        await api.patch(`/vehicles/${v.id}`, {
          plate: editForm.plate, model: editForm.model, color: editForm.color, driver_id: drv.id,
        });
      } else if (!hasVeh && vehWanted) {
        await api.post("/vehicles", {
          plate: editForm.plate || "—", model: editForm.model || "—",
          color: editForm.color || null, driver_id: drv.id,
        });
      } else if (hasVeh && !vehWanted) {
        await api.delete(`/vehicles/${v.id}`);
      }
      toast.success("Saved");
      cancelEdit();
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const delDriver = async (id) => {
    if (!confirm("Delete this driver and their vehicle?")) return;
    try {
      const v = vehicleByDriver[id];
      if (v) await api.delete(`/vehicles/${v.id}`);
      await api.delete(`/users/${id}`);
      toast.success("Driver removed");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  /* ---------- (Legacy phone-share link removed; we use email invite now.) ---------- */

  return (
    <div className="p-6 lg:p-10 max-w-[1700px] mx-auto" data-testid="admin-drivers-page">
      <header className="flex items-end justify-between mb-6 gap-3 flex-wrap">
        <div>
          <h1 className="text-3xl lg:text-4xl font-display font-bold tracking-tight">Drivers</h1>
          <p className="text-muted-foreground mt-1.5">Manage your delivery team and their vehicles in one place.</p>
        </div>
        <Dialog open={openAdd} onOpenChange={setOpenAdd}>
          <DialogTrigger asChild>
            <Button className="rounded-full" data-testid="add-driver-btn"><UserPlus className="h-4 w-4 mr-1.5" /> Add driver</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>New driver & vehicle</DialogTitle></DialogHeader>
            <form onSubmit={submitAdd} className="space-y-3" data-testid="add-driver-form">
              <div><Label>Full name</Label><Input required value={addForm.name} onChange={(e) => setAddForm({ ...addForm, name: e.target.value })} data-testid="drv-name" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Email</Label><Input type="email" required value={addForm.email} onChange={(e) => setAddForm({ ...addForm, email: e.target.value })} data-testid="drv-email" /></div>
                <div><Label>Phone</Label><Input value={addForm.phone} onChange={(e) => setAddForm({ ...addForm, phone: formatPhoneInput(e.target.value) })} placeholder="305-555-1234" data-testid="drv-phone" /></div>
              </div>
              <div><Label>Password</Label><Input type="password" required minLength={6} value={addForm.password} onChange={(e) => setAddForm({ ...addForm, password: e.target.value })} data-testid="drv-password" /></div>
              <div className="text-xs uppercase tracking-wider text-muted-foreground pt-2">Vehicle (optional)</div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Plate</Label><Input value={addForm.plate} onChange={(e) => setAddForm({ ...addForm, plate: e.target.value })} data-testid="veh-plate" /></div>
                <div><Label>Model</Label><Input value={addForm.model} onChange={(e) => setAddForm({ ...addForm, model: e.target.value })} data-testid="veh-model" /></div>
              </div>
              <div>
                <Label>Vehicle color</Label>
                <Select value={addForm.color} onValueChange={(v) => setAddForm({ ...addForm, color: v })}>
                  <SelectTrigger data-testid="veh-color"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {COLOR_OPTIONS.map((c) => (
                      <SelectItem key={c.v} value={c.v}>
                        <span className="inline-flex items-center gap-2">
                          <span className="h-3 w-3 rounded-full border border-border" style={{ background: c.hex }} />{c.v}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button type="submit" disabled={busy} className="w-full rounded-full" data-testid="drv-submit">{busy ? "Saving…" : "Add driver"}</Button>
            </form>
          </DialogContent>
        </Dialog>
      </header>

      <Card className="rounded-xl border-border bg-card/80 overflow-hidden" data-testid="drivers-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary/50 text-left text-xs uppercase tracking-wider">
              <tr>
                <th className="px-4 py-3 font-medium">Driver</th>
                <th className="px-4 py-3 font-medium">Email</th>
                <th className="px-4 py-3 font-medium">Phone</th>
                <th className="px-4 py-3 font-medium">Plate</th>
                <th className="px-4 py-3 font-medium">Model</th>
                <th className="px-4 py-3 font-medium">Vehicle Color</th>
                <th className="px-4 py-3 font-medium text-center">Active</th>
                <th className="px-4 py-3 text-right"></th>
              </tr>
            </thead>
            <tbody>
              {drivers.map((d) => {
                const v = vehicleByDriver[d.id] || {};
                const isActive = d.active !== false;
                return (
                  <tr key={d.id} className={`border-t border-border hover:bg-secondary/30 ${!isActive ? "opacity-60" : ""}`} data-testid={`driver-row-${d.id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2.5">
                        <div className="h-8 w-8 rounded-full bg-primary/10 text-primary grid place-items-center shrink-0"><UserCircle2 className="h-5 w-5" /></div>
                        <span className="font-medium" data-testid={`driver-name-${d.id}`}>{d.name}</span>
                        {!isActive && <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground" data-testid={`driver-inactive-badge-${d.id}`}>Inactive</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground text-xs">{d.email}</td>
                    <td className="px-4 py-3 text-muted-foreground font-mono-num text-xs">{d.phone ? formatPhone(d.phone) : "—"}</td>
                    <td className="px-4 py-3 font-medium" data-testid={`driver-plate-${d.id}`}>{v.plate || "—"}</td>
                    <td className="px-4 py-3 text-muted-foreground">{v.model || "—"}</td>
                    <td className="px-4 py-3" data-testid={`driver-color-${d.id}`}><ColorSwatch name={v.color} /></td>
                    <td className="px-4 py-3 text-center">
                      <Checkbox
                        checked={isActive}
                        onCheckedChange={(val) => toggleActive(d, !!val)}
                        data-testid={`driver-active-toggle-${d.id}`}
                        aria-label={isActive ? "Deactivate driver" : "Activate driver"}
                      />
                    </td>
                    <td className="px-3 py-3 text-right whitespace-nowrap">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => sendInviteEmail(d)}
                        disabled={emailingId === d.id || !d.email}
                        className="text-muted-foreground hover:text-primary h-8 w-8"
                        data-testid={`driver-invite-${d.id}`}
                        title={d.email ? `Email app invite to ${d.email}` : "No email on file"}
                      >
                        <Mail className={`h-4 w-4 ${emailingId === d.id ? "animate-pulse" : ""}`} />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => startEdit(d)} className="text-muted-foreground hover:text-primary h-8 w-8" data-testid={`driver-edit-${d.id}`} title="Edit">
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => delDriver(d.id)} className="text-muted-foreground hover:text-destructive h-8 w-8" data-testid={`driver-delete-${d.id}`} title="Delete">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
              {drivers.length === 0 && (
                <tr><td colSpan={8} className="px-5 py-12 text-center text-muted-foreground text-sm">No drivers yet — click "Add driver" to create one.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Edit dialog */}
      <Dialog open={!!editRow} onOpenChange={(o) => !o && cancelEdit()}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit driver & vehicle</DialogTitle></DialogHeader>
          {editRow && (
            <form onSubmit={submitEdit} className="space-y-3" data-testid="edit-driver-form">
              <div><Label>Full name</Label><Input required value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} data-testid="edit-drv-name" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Email</Label><Input type="email" required value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} data-testid="edit-drv-email" /></div>
                <div><Label>Phone</Label><Input value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: formatPhoneInput(e.target.value) })} placeholder="305-555-1234" data-testid="edit-drv-phone" /></div>
              </div>
              <div><Label>New password (leave blank to keep)</Label><Input type="password" value={editForm.password} onChange={(e) => setEditForm({ ...editForm, password: e.target.value })} data-testid="edit-drv-password" /></div>
              <div className="text-xs uppercase tracking-wider text-muted-foreground pt-2">Vehicle</div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Plate</Label><Input value={editForm.plate} onChange={(e) => setEditForm({ ...editForm, plate: e.target.value })} data-testid="edit-veh-plate" /></div>
                <div><Label>Model</Label><Input value={editForm.model} onChange={(e) => setEditForm({ ...editForm, model: e.target.value })} data-testid="edit-veh-model" /></div>
              </div>
              <div>
                <Label>Vehicle color</Label>
                <Select value={editForm.color} onValueChange={(v) => setEditForm({ ...editForm, color: v })}>
                  <SelectTrigger data-testid="edit-veh-color"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {COLOR_OPTIONS.map((c) => (
                      <SelectItem key={c.v} value={c.v}>
                        <span className="inline-flex items-center gap-2">
                          <span className="h-3 w-3 rounded-full border border-border" style={{ background: c.hex }} />{c.v}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button type="submit" disabled={busy} className="w-full rounded-full" data-testid="edit-drv-submit">{busy ? "Saving…" : "Save changes"}</Button>
            </form>
          )}
        </DialogContent>
      </Dialog>
      {/* Email-sent / fallback link dialog (mirrors AdminUsers) */}
      <Dialog open={!!inviteResult} onOpenChange={(o) => !o && setInviteResult(null)}>
        <DialogContent data-testid="invite-result-dialog">
          <DialogHeader>
            <DialogTitle>
              {inviteResult?.email_sent
                ? `Invite emailed to ${inviteResult?.user?.name}`
                : `Couldn't email ${inviteResult?.user?.name}`}
            </DialogTitle>
          </DialogHeader>
          {inviteResult && (
            <div className="space-y-4">
              {inviteResult.email_sent ? (
                <div className="rounded-lg border border-accent/40 bg-accent/10 text-accent-foreground/90 px-3 py-2.5 text-sm flex items-start gap-2" data-testid="invite-success-banner">
                  <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0 text-accent" />
                  <span>
                    Email sent to <strong>{inviteResult.to}</strong>. They'll be auto-signed in for{" "}
                    <strong>{inviteResult.expires_in_days} days</strong>.
                  </span>
                </div>
              ) : (
                <div className="rounded-lg border border-amber-300 bg-amber-50 text-amber-900 px-3 py-2.5 text-sm flex items-start gap-2" data-testid="invite-fallback-banner">
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                  <div>
                    <div className="font-medium">Couldn't deliver the email automatically.</div>
                    <div className="text-xs mt-0.5">{inviteResult.error || "Unknown error."}</div>
                    {inviteResult.fallback_url && (
                      <>
                        <div className="text-xs mt-1.5">Meanwhile, copy this magic link — it auto-signs them in for {inviteResult.expires_in_days} days.</div>
                        <Input value={inviteResult.fallback_url} readOnly className="text-xs font-mono mt-2" onFocus={(e) => e.target.select()} />
                      </>
                    )}
                  </div>
                </div>
              )}
              <div className="text-[11px] text-muted-foreground bg-secondary/50 rounded-lg p-3">
                <span className="font-medium text-foreground">Tip:</span> tell the driver to tap their browser's <span className="font-medium">"Add to Home Screen"</span> option after first sign-in so SRCI Pharm-Route looks like a real app.
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
